import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin, Briefcase, IndianRupee } from "lucide-react";
import api from "../lib/api";
import { Reveal, SectionLabel } from "./motion";

const INDUSTRIES = ["All Industries", "Technology", "Finance", "Healthcare", "Engineering", "Marketing", "Retail", "Legal", "Consulting"];

export default function Jobs({ filters, setFilters, onApply }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/jobs", {
          params: {
            q: filters.q || "",
            location: filters.location || "",
            industry: filters.industry === "All Industries" ? "" : filters.industry || "",
          },
        });
        setJobs(data);
      } catch {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  return (
    <section id="jobs" className="bg-white py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="jobs-section">
      <SectionLabel number="02" text="Featured Jobs" />
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
        <Reveal>
          <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0A1128]">
            Open roles,<br />right now.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="w-full lg:w-auto">
          <div className="flex flex-col sm:flex-row gap-3" data-testid="jobs-filter-bar">
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder="Search title or company"
              className="border border-[#0A1128]/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors w-full sm:w-56"
              data-testid="jobs-filter-title"
            />
            <input
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="Location"
              className="border border-[#0A1128]/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors w-full sm:w-44"
              data-testid="jobs-filter-location"
            />
            <select
              value={filters.industry}
              onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
              className="border border-[#0A1128]/15 bg-transparent px-4 py-3 text-sm outline-none focus:border-[#1D4ED8] transition-colors"
              data-testid="jobs-filter-industry"
            >
              {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
            </select>
          </div>
        </Reveal>
      </div>

      <div data-testid="jobs-list">
        {loading ? (
          <div className="py-20 text-center text-[#475569] text-sm uppercase tracking-[0.2em]" data-testid="jobs-loading">Loading roles…</div>
        ) : jobs.length === 0 ? (
          <div className="py-20 border-y border-[#0A1128]/10 text-center" data-testid="jobs-empty">
            <p className="font-display text-2xl text-[#0A1128] mb-2">No roles match your search.</p>
            <p className="text-[#475569] text-sm">Try widening the filters — or send us your resume and we'll find you one.</p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {jobs.map((job, i) => (
              <motion.div
                key={job.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className="group border-t border-[#0A1128]/10 last:border-b hover:bg-[#F4F5F7] transition-colors duration-300"
                data-testid={`job-row-${job.id}`}
              >
                <div className="py-8 md:py-10 grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3 mb-2">
                      {job.featured && (
                        <span className="text-[10px] uppercase tracking-[0.2em] bg-[#1D4ED8] text-white px-2 py-1">Featured</span>
                      )}
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">{job.industry}</span>
                    </div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight text-[#0A1128] group-hover:text-[#1D4ED8] transition-colors duration-300">
                      {job.title}
                    </h3>
                    <p className="text-sm text-[#475569] mt-1">{job.company}</p>
                  </div>
                  <div className="col-span-12 md:col-span-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#475569]">
                    <span className="flex items-center gap-2"><MapPin size={14} className="text-[#1D4ED8]" />{job.location}</span>
                    <span className="flex items-center gap-2"><Briefcase size={14} className="text-[#1D4ED8]" />{job.type}</span>
                    {job.salary && <span className="flex items-center gap-2"><IndianRupee size={14} className="text-[#1D4ED8]" />{job.salary.replace(/[₹\s]/g, "")}</span>}
                  </div>
                  <div className="col-span-12 md:col-span-3 flex md:justify-end">
                    <button
                      onClick={() => onApply(job)}
                      className="flex items-center gap-2 border border-[#0A1128]/20 px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#0A1128] group-hover:bg-[#1D4ED8] group-hover:border-[#1D4ED8] group-hover:text-white transition-all duration-300"
                      data-testid={`apply-button-${job.id}`}
                    >
                      Apply Now
                      <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </section>
  );
}
